/* eslint-disable react-hooks/exhaustive-deps -- effets alignés chargement id */
import React, { useEffect, useReducer, useState } from 'react';
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';

import U2000DefaultForm from '../../u2000Core/components/formControls/U2000DefaultForm';

import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000GetHandlers } from '../../u2000Core/hooks/handler/U2000UseGetHandlers';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import type { U2000SearchPayload, U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000SaveHandlers } from '../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from '../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3149ROLES } from '../U3149';
import U3149WorkNatureBody from './form/u3149WorkNature/U3149WorkNatureBody';
import { U3149WorkNatureFormToolBar } from './form/u3149WorkNature/U3149WorkNatureFormToolBar';
import U3149WorkNatureDeleteDlg from './common/U3149WorkNatureDeleteDlg';
import { useU3149Context } from '../U3149Context';
import U2000AuditPanelComponent from '../../u2000Core/components/audit/U2000AuditPanelComponent';
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  createEmptyU3149FormRow,
  luHandleAppiError,
  normalizeU3149FromApi,
  u3149FormRowToSearchRow,
  u3149PrunePunchableTaskIdsToActiveCatalog,
  type U3149WorkNature,
  type U3149WorkNatureFormRow,
  type U3149WorkNatureSearchRow,
} from '../U3149Service';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U2000Tab from '../../u2000Core/components/tab/U2000Tab';
import U3149WorkNaturePunchableTasksBody from './form/u3149WorkNature/U3149WorkNaturePunchableTasksBody';

function translationFieldKey(index: number, field: 'languageCode' | 'workNatureDescription'): string {
  return `translations.${index}.${field}`;
}

function normalizeForForm(row: U3149WorkNature): U3149WorkNatureFormRow {
  return normalizeU3149FromApi(row);
}

function validateU3149Form(
  row: U3149WorkNatureFormRow,
  t: (key: string, opts?: Record<string, unknown>) => string,
  nameSpace: string,
  isFr: boolean,
  editMode: U2000FormStateEnumEditMode,
): Record<string, string> {
  const result: Record<string, string> = {};

  if (editMode === U2000FormStateEnumEditMode.ADD) {
    const code = row.workNatureCode?.trim() ?? '';
    if (!code) {
      result['workNatureCode'] = t('errWorkNatureCodeRequired', {
        ns: nameSpace,
        defaultValue: isFr ? 'Le code est obligatoire.' : 'Code is required.',
      });
    } else if (code.length > 15) {
      result['workNatureCode'] = t('errWorkNatureCodeMax', {
        ns: nameSpace,
        defaultValue: isFr ? 'Le code ne peut pas dépasser 15 caractères.' : 'Code cannot exceed 15 characters.',
      });
    }
  }

  const sortOrder = row.sortOrder;
  if (sortOrder === undefined || sortOrder === null || String(sortOrder).trim() === '') {
    result['sortOrder'] = t('errSortOrderRequired', {
      ns: nameSpace,
      defaultValue: isFr ? "L'ordre d'affichage est obligatoire." : 'Display order is required.',
    });
  } else if (!Number.isInteger(Number(sortOrder))) {
    result['sortOrder'] = t('errSortOrderInteger', {
      ns: nameSpace,
      defaultValue: isFr ? "L'ordre d'affichage doit être un entier." : 'Display order must be an integer.',
    });
  }

  if (row.active === undefined || row.active === null) {
    result['active'] = t('errActiveRequired', {
      ns: nameSpace,
      defaultValue: isFr ? "L'état actif est obligatoire." : 'Active status is required.',
    });
  }

  if (!row.translations || row.translations.length === 0) {
    result['translations'] = t('errTranslationsMin', {
      ns: nameSpace,
      defaultValue: isFr ? 'Au moins une traduction est requise.' : 'At least one translation is required.',
    });
    return result;
  }

  const allowedLangs = new Set(['fr', 'en']);
  const langsSeen = new Set<string>();
  const langsPresent = new Set<string>();

  row.translations.forEach((tr, i) => {
    const lang = tr.languageCode?.trim().toLowerCase() ?? '';
    if (!lang) {
      result[translationFieldKey(i, 'languageCode')] = t('errLanguageCodeRequired', {
        ns: nameSpace,
        defaultValue: isFr ? 'La langue est obligatoire.' : 'Language is required.',
      });
    } else if (!allowedLangs.has(lang)) {
      result[translationFieldKey(i, 'languageCode')] = t('errLanguageCodeChoice', {
        ns: nameSpace,
        defaultValue: isFr ? 'Choisissez FR ou EN.' : 'Choose FR or EN.',
      });
    } else if (langsSeen.has(lang)) {
      result[translationFieldKey(i, 'languageCode')] = t('errLanguageCodeDuplicate', {
        ns: nameSpace,
        defaultValue: isFr ? "Chaque code langue ne doit apparaître qu'une seule fois." : 'Each language code must be unique.',
      });
    } else {
      langsSeen.add(lang);
      langsPresent.add(lang);
    }

    const label = tr.workNatureDescription?.trim() ?? '';
    if (!label) {
      result[translationFieldKey(i, 'workNatureDescription')] = t('errWorkNatureDescriptionRequired', {
        ns: nameSpace,
        defaultValue: isFr ? 'La description est obligatoire.' : 'Description is required.',
      });
    } else if (label.length > 255) {
      result[translationFieldKey(i, 'workNatureDescription')] = t('errWorkNatureDescriptionMax', {
        ns: nameSpace,
        defaultValue: isFr ? 'La description ne peut pas dépasser 255 caractères.' : 'Description cannot exceed 255 characters.',
      });
    }

    const hasId = Boolean(tr.id && String(tr.id).trim() !== '');
    if (hasId && (tr.version === undefined || tr.version === null || !Number.isFinite(Number(tr.version)))) {
      result[`translations.${i}.version`] = t('errTranslationVersionRequired', {
        ns: nameSpace,
        defaultValue: isFr ? 'Version manquante pour une traduction existante.' : 'Missing version for an existing translation.',
      });
    }
  });

  if (!langsPresent.has('fr')) {
    result['translations'] = t('errTranslationFrRequired', {
      ns: nameSpace,
      defaultValue: isFr ? 'La traduction française est obligatoire.' : 'French translation is required.',
    });
  } else if (!langsPresent.has('en')) {
    result['translations'] = t('errTranslationEnRequired', {
      ns: nameSpace,
      defaultValue: isFr ? 'La traduction anglaise est obligatoire.' : 'English translation is required.',
    });
  }

  return result;
}

export interface U3149WorkNatureFormExtendedHandlers extends U2000FormStateHandlersReturn<U3149WorkNatureFormRow> {
  addNewWorkNature: () => void;
  modifyWorkNature: () => void;
  deleteWorkNature: () => Promise<void>;
}

const U3149WorkNatureForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { initLU, luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3149Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith('fr');
  const listTitle = t('title', { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const u3149BL: U2000FormSBusinessHandlers<U3149WorkNatureFormRow> = {
    newRecord: () => createEmptyU3149FormRow(),

    validateForm: (row: U3149WorkNatureFormRow) =>
      validateU3149Form(row, t, NS, isFr, u3149FormExtended.getFormEditMode()),

    saveForme: async (row: U3149WorkNatureFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateU3149Form(row, t, NS, isFr, editMode);
      if (Object.keys(errors).length > 0) {
        u3149FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },

    cancelForm: () => {
      const old = u3149FormExtended.getOldData() as U3149WorkNatureFormRow | null;
      if (old) {
        u3149FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === '/U3149') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    onLeaveForm: () => {
      navigate('/U3149');
    },
  };

  const u3149FormHandlers = useU2000FormStateHandlers<U3149WorkNatureFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3149BL,
  });

  const u3149FormExtended: U3149WorkNatureFormExtendedHandlers = {
    ...u3149FormHandlers,
    addNewWorkNature: () => {
      u3149FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyWorkNature: () => {
      u3149FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteWorkNature: async () => {
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3149WorkNatureSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3149WorkNatureSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3149WorkNatureFormRow>({
    doGetFn: (rowId: string) => {
      return doGetData(u2000AxiosAuthenticated, rowId);
    },
    onSuccess: (row: U3149WorkNatureFormRow) => {
      const normalized = normalizeForForm(row as unknown as U3149WorkNature);
      normalized.punchableTaskIds = u3149PrunePunchableTaskIdsToActiveCatalog(
        normalized.punchableTaskIds,
        initLU.activeTasks,
      );
      const existing = gridStateHandlers.getData().find((r) => r.id === normalized.id);
      gridStateHandlers.setDataById(normalized.id, u3149FormRowToSearchRow(normalized, existing));
      u3149FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const firstLabel = normalized.translations?.[0]?.workNatureDescription ?? '';
      const crumbLabel = firstLabel ? `${normalized.workNatureCode} — ${firstLabel}` : normalized.workNatureCode;
      setLogicalUnitBreadcrumb([{ href: '/U3149', label: listTitle }, { label: crumbLabel }]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3149FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3149WorkNatureFormRow>({
    doSaveFn: (row: U3149WorkNatureFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3149WorkNatureFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeForForm(row as unknown as U3149WorkNature);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), u3149FormRowToSearchRow(normalized)]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        const existing = gridStateHandlers.getData().find((r) => r.id === row.id);
        gridStateHandlers.setDataById(row.id, u3149FormRowToSearchRow(normalized, existing));
      }
      u3149FormExtended.afterSaveData(normalized);
      toast.success(t('msgLogicalUnitSaved', { ns: NS }));

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === '/U3149') {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3149FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3149FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3149FormExtended.getData()],
    doDeleteFn: async (rowId, row) => {
      const r = row as U3149WorkNatureFormRow;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(r));
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        toast.success(t('success0003', { key: (del as U3149WorkNatureFormRow).workNatureCode, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate('/U3149', { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === 'new') {
      u3149FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3149FormExtended.setData(createEmptyU3149FormRow());
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  if (u3149FormExtended.getData() === null) {
    return null;
  }

  const editMode = u3149FormExtended.getFormEditMode();
  const naturePersisted =
    editMode !== U2000FormStateEnumEditMode.ADD &&
    Boolean(u3149FormExtended.getData()?.id && String(u3149FormExtended.getData()?.id).trim() !== '');

  const formTabs = [
    naturePersisted && {
      id: 'tabPunchableTasks',
      label: t('tabPunchableTasks', { ns: NS }),
      content: (
        <U3149WorkNaturePunchableTasksBody
          formStateHandlers={u3149FormExtended}
          t={t}
          language={i18n.resolvedLanguage ?? i18n.language}
        />
      ),
    },
    hasRole(U3149ROLES.AUDITS) && {
      id: 'tabAudits',
      label: t('tabAudits', { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={u3149FormExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U3149WorkNatureAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3149WorkNatureDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3149WorkNatureFormToolBar formStateHandlers={u3149FormExtended} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3149FormExtended as any}
      >
        <U3149WorkNatureBody formStateHandlers={u3149FormExtended} t={t} />

        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={formTabs[0]?.id}
            activeTab={activeTab ?? formTabs[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === 'string' ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3149WorkNatureForm;
